
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Trash2, MessageSquare, UserCheck, UserX, Phone, Mail } from "lucide-react";
import { Patient } from "@/types/patient.types";
import { PatientDetails } from "./PatientDetails";
import { usePatientManagement } from "../usePatientManagement";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface PatientCardProps {
  relation: Patient;
  messages: any[];
  isLoadingMessages: boolean;
  onRefresh: () => void;
  isSelected?: boolean;
  onToggleDetails?: () => void;
}

export const PatientCard = ({ 
  relation, 
  messages, 
  isLoadingMessages, 
  onRefresh,
  isSelected = false,
  onToggleDetails
}: PatientCardProps) => {
  const [showDetails, setShowDetails] = useState(false);
  const { updatePatientStatus, deletePatient } = usePatientManagement();
  const { toast } = useToast();

  const handleToggleDetails = () => {
    if (onToggleDetails) {
      onToggleDetails();
    } else {
      setShowDetails(!showDetails);
    }
  };

  const handleStatusUpdate = async (status: 'approved' | 'rejected') => {
    try {
      await updatePatientStatus.mutateAsync({
        patientId: relation.patient?.id as string,
        status
      });
      toast({
        title: "Statut mis à jour",
        description: `Patient ${status === 'approved' ? 'approuvé' : 'rejeté'} avec succès`,
      });
      onRefresh();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de mettre à jour le statut du patient",
      });
    }
  };

  const handleDeletePatient = async () => {
    try {
      await deletePatient.mutateAsync(relation.patient?.id as string);
      toast({
        title: "Patient supprimé",
        description: "Le patient a été supprimé avec succès",
      });
      onRefresh();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le patient",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'Approuvé';
      case 'pending': return 'En attente';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  const detailsVisible = onToggleDetails ? isSelected : showDetails;

  return (
    <>
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {relation.patient?.full_name || "Patient inconnu"}
            </CardTitle>
            <Badge className={getStatusColor(relation.status || 'pending')}>
              {getStatusText(relation.status || 'pending')}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="truncate">{relation.patient?.email || "Non renseigné"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span>{relation.patient?.phone_number || "Non renseigné"}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleDetails}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              {detailsVisible ? "Masquer les détails" : "Voir les détails"}
            </Button>

            {relation.status === 'pending' && (
              <>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleStatusUpdate('approved')}
                  className="flex items-center gap-2"
                  disabled={updatePatientStatus.isPending}
                >
                  <UserCheck className="h-4 w-4" />
                  Approuver
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStatusUpdate('rejected')}
                  className="flex items-center gap-2"
                  disabled={updatePatientStatus.isPending}
                >
                  <UserX className="h-4 w-4" />
                  Rejeter
                </Button>
              </>
            )}

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  size="sm"
                  className="flex items-center gap-2"
                  disabled={deletePatient.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer le patient</AlertDialogTitle>
                  <AlertDialogDescription>
                    Êtes-vous sûr de vouloir supprimer ce patient ? Cette action est irréversible.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeletePatient}>
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {detailsVisible && (
            <PatientDetails
              relation={relation}
              messages={messages}
              isLoadingMessages={isLoadingMessages}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
};
